<template>
  <div
    v-if="!editionMode"
    class="flex flex-row items-start gap-3 rounded border p-3"
  >
    <div class="shrink-0">
      <span
        :tint="reasonComputed.bg_color"
        class="bg-primary text-contrast-primary rounded-md px-2"
      >
        {{ reasonComputed.name }}
      </span>
    </div>
    <div class="flex flex-1 flex-col">
      <div class="text-base font-bold">
        {{ $h.formatDate(item.start_at, 'LL') }} -
        {{ $h.formatDate(item.end_at, 'LL') }}
      </div>
      <div class="bg-primary-100 rounded p-1 text-xs empty:hidden">
        {{ item.remarks }}
      </div>
    </div>
    <div class="flex flex-row gap-2">
      <BaseButton size="extra-small" icon="edit" @click="editionMode = true" />
      <BaseButton
        color="danger"
        icon="trash"
        size="extra-small"
        @click="deleteItem"
      />
    </div>
  </div>
  <div v-else class="flex w-full flex-col gap-3 rounded border p-3">
    <div class="w-full">
      <SelectComponent
        v-model="localItem.reason"
        :label="$t('global.reason')"
        name="reason"
        :values="availabilityReasons"
        :required="true"
        :searchable="false"
        :edition-mode="isDisabled"
        track-by="id"
        display-key="name"
      />
    </div>
    <div v-if="!item.id" class="flex w-full">
      <WalkmanButton
        v-model:model-value="localTimeType"
        :label="$t('attributes.type')"
        :options="timeTypes"
      />
    </div>

    <div v-if="isTimeAllDay" class="flex w-full">
      <div class="w-1/3">
        <BaseShowEditDatePickerv2
          v-model:date="localDateStart"
          :label="$t('attributes.date')"
          :required="true"
          :edition-mode="isDisabled"
        />
      </div>
    </div>

    <div
      v-if="isTimePeriod"
      class="flex flex-row items-center justify-between gap-3"
    >
      <div class="w-full">
        <BaseShowEditDatePickerv2
          v-model:date="localDateStart"
          :label="$t('global.start_at')"
          :required="true"
          :edition-mode="true"
          :max-date="localDateEnd"
        />
      </div>

      <div class="w-full">
        <BaseShowEditDatePickerv2
          v-model:date="localDateEnd"
          :label="$t('global.end_at')"
          :required="true"
          :edition-mode="true"
          :min-date="localDateStart"
        />
      </div>
    </div>

    <div
      v-if="isTimeCustom"
      class="flex flex-row items-center justify-between gap-3"
    >
      <div class="w-full">
        <BaseShowEditDateTimePickerv2
          v-model:date="localDateStart"
          :label-date="$t('global.start_at')"
          :required="true"
          :edition-mode="isDisabled"
          :max-date="localDateEnd"
        />
      </div>

      <div class="w-full">
        <BaseShowEditDateTimePickerv2
          v-model:date="localDateEnd"
          :label-date="$t('global.end_at')"
          :required="true"
          :edition-mode="isDisabled"
          :min-date="localDateStart"
          :default-time="'23:59'"
        />
      </div>
    </div>

    <div class="w-full">
      <BaseTextArea
        v-model="localItem.remarks"
        :name="'remarks'"
        :label="$t('attributes.remarks')"
        :edition-mode="isDisabled"
      />
    </div>

    <div class="flex w-full items-center justify-end gap-2">
      <BaseButton
        color="danger"
        :title="$t('button.delete')"
        size="small"
        icon="trash"
        :disabled="loading"
        :display-loader="loading"
        @click="deleteItem"
      />
      <BaseButton
        v-if="editionMode"
        :title="item.id ? $t('button.edit') : $t('button.add')"
        size="small"
        color="warning"
        icon="edit"
        :disabled="!isComplete || loading"
        :display-loader="sent || loading"
        @click="submit"
      />
      <BaseButton
        v-else
        :title="$t('button.edit')"
        size="small"
        :disabled="loading"
        :display-loader="loading"
        @click="editionMode = true"
      />
    </div>
    <ConfirmModal ref="deleteAvailibilityItem" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseShowEditDateTimePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateTimePickerv2.vue'
import BaseTextArea from '@c/addf-package/components/BaseInput/BaseTextArea.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import BaseShowEditDatePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDatePickerv2.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'

export default {
  name: 'AvailabilityItemCard',
  components: {
    ConfirmModal,
    BaseShowEditDatePickerv2,
    WalkmanButton,
    BaseButton,
    BaseTextArea,
    BaseShowEditDateTimePickerv2,
    SelectComponent
  },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  emits: ['add:item', 'update:item', 'refresh-list', 'delete-item'],
  data() {
    return {
      editionMode: !this.item.id,
      timeUndefined: new Date(),
      sent: false,
      loading: false,
      timeType: 'all_day',
      timeTypes: [
        {
          value: 'all_day',
          label: this.$t('attributes.all_day')
        },
        {
          value: 'period',
          label: this.$t('global.period')
        },
        {
          value: 'custom',
          label: this.$t('global.customize')
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      availabilityReasons: 'metadata/getMetadataAvailabilityReasons'
    }),
    reasonComputed() {
      return (
        this.availabilityReasons.find((item) => item.id === this.item.reason) ??
        null
      )
    },
    localItem: {
      get() {
        return this.item
      },
      set(value) {
        this.$emit('update:item', value)
      }
    },
    localTimeType: {
      get() {
        return this.timeType
      },
      set(value) {
        this.localDateStart = null
        this.localDateEnd = null

        this.timeType = value
      }
    },
    localDateStart: {
      get() {
        return this.item.start_at
      },
      set(value) {
        this.localItem.start_at = this.withConcat(value)

        if (this.isTimeAllDay && value) {
          this.localDateEnd = value
        }
      }
    },
    localDateEnd: {
      get() {
        return this.item.end_at
      },
      set(value) {
        this.localItem.end_at = this.withConcat(value, false)
      }
    },
    isDisabled() {
      return this.sent || this.editionMode
    },
    isTimeCustom() {
      return !!this.item.id || this.timeType === 'custom'
    },
    isTimePeriod() {
      return !this.item.id && this.timeType === 'period'
    },
    isTimeAllDay() {
      return !this.item.id && this.timeType === 'all_day'
    },
    isComplete() {
      if (!this.item.reason) {
        return false
      } else if (!this.localDateStart) {
        return false
      } else if (!this.localDateEnd) {
        return false
      } else if (this.localDateStart === this.localDateEnd) {
        return false
      }

      return true
    }
  },
  mounted() {
    if (!this.item.id) {
      this.editionMode = true
    }
  },
  methods: {
    ...mapActions({
      proceessAddUnavailabilityItemsAction:
        'unavailability/addUnavailabilityItem',
      proceessUpdateUnavailabilityItemAction:
        'unavailability/updateUnavailabilityItem'
    }),
    async deleteItem() {
      let confirm = await this.$refs.deleteAvailibilityItem.show({
        title: this.$t('validation.confirm_delete'),
        okButton: this.$t('global.yes')
      })
      if (confirm) {
        this.$emit('delete-item', this.item.id)
      }
    },
    submit() {
      this.loading = true
      if (this.item.id) {
        this.proceessUpdateUnavailabilityItemAction({
          id: this.item.id,
          data: this.item
        })
          .then((response) => {
            if (response.success) {
              this.editionMode = false
              this.$emit('refresh-list')
            }
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.proceessAddUnavailabilityItemsAction({
          data: this.item
        })
          .then((response) => {
            if (response.success) {
              this.editionMode = false
              this.$emit('refresh-list')
            }
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    withConcat(value, isStart = true) {
      if (value) {
        if (this.isTimeAllDay || this.isTimePeriod) {
          const concat = isStart ? '00:00' : '23:59'

          return `${value} ${concat}`
        }

        return value
      }

      return null
    }
  }
}
</script>
